import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Button,
  Empty,
  List,
  Modal,
  Segmented,
  Space,
  Spin,
  Switch,
  Tag,
  Typography,
} from "antd";
import { ArrowRightOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { AssistanceApi, DidacticRoutesAPI } from "../../../../api";
import { useAuth } from "../../../../hooks";

import "./RutasStep.scss";

const assistanceApi = new AssistanceApi();
const didacticRoutesAPI = new DidacticRoutesAPI();
const EDUCA360_USER_ID = "67921ab3740e0f2aba258111";

export function RutasDidacticasFormStep2({
  accessToken,
  teacher,
  route,
  onReload,
  onClose,
  onNext,
}) {
  const { t } = useTranslation("itineraries");
  const { user } = useAuth();
  const userId = user?.user?._id;

  const [loadingList, setLoadingList] = useState(false);
  const [saving, setSaving] = useState(false);

  const [assistants, setAssistants] = useState([]);
  const [selectedAssistants, setSelectedAssistants] = useState([]);
  const [initialAssignedIds, setInitialAssignedIds] = useState([]);

  const [viewMode, setViewMode] = useState("edu360"); // edu360 | mine

  const routeId = route?._id;

  const assignedIds = useMemo(() => {
    const list = Array.isArray(route?.assistants) ? route.assistants : [];
    return list.map((a) => a.assistant).filter(Boolean);
  }, [route?.assistants]);

  // ✅ FIX ESLINT: deps completas
  useEffect(() => {
    setSelectedAssistants(assignedIds);
    setInitialAssignedIds(assignedIds);
  }, [routeId, assignedIds]);

  const fetchAssistants = useCallback(async () => {
    if (!accessToken) return;
    if (viewMode === "mine" && !userId) return;

    try {
      setLoadingList(true);
      const response = await assistanceApi.getAssistances(accessToken, 1, 100);
      let fetched = response?.assistances?.docs || [];

      fetched =
        viewMode === "edu360"
          ? fetched.filter((a) => a.user === EDUCA360_USER_ID)
          : fetched.filter((a) => a.user === userId);

      setAssistants(fetched);
    } catch (error) {
      Modal.error({
        title: t("form.assistantsLoadError"),
        content: error?.message || t("wizard.unexpectedError"),
        centered: true,
      });
    } finally {
      setLoadingList(false);
    }
  }, [accessToken, userId, viewMode, t]);

  useEffect(() => {
    fetchAssistants();
  }, [fetchAssistants]);

  const selectedSet = useMemo(
    () => new Set(selectedAssistants),
    [selectedAssistants],
  );

  const handleToggleAssistant = useCallback((assistantId) => {
    setSelectedAssistants((prev) =>
      prev.includes(assistantId)
        ? prev.filter((id) => id !== assistantId)
        : [...prev, assistantId],
    );
  }, []);

  const arraysEqual = useCallback((a, b) => {
    if (a.length !== b.length) return false;
    const setB = new Set(b);
    return a.every((x) => setB.has(x));
  }, []);

  const handleSaveAndNext = useCallback(async () => {
    if (!routeId) return;

    setSaving(true);
    try {
      const hasChanges = !arraysEqual(selectedAssistants, initialAssignedIds);
      if (hasChanges) {
        await didacticRoutesAPI.insertAssistantsInRoute(
          accessToken,
          routeId,
          selectedAssistants,
        );
        onReload?.();
      }
      onNext?.();
    } catch (error) {
      Modal.error({
        title: t("form.assistantsSaveError"),
        content: error?.message || t("wizard.unexpectedError"),
        centered: true,
      });
    } finally {
      setSaving(false);
    }
  }, [
    accessToken,
    arraysEqual,
    initialAssignedIds,
    onNext,
    onReload,
    routeId,
    selectedAssistants,
    t,
  ]);

  const countTag = useMemo(() => {
    const c = selectedAssistants.length;
    return (
      <Tag className="rutas-step2__count-tag">
        {c} {c === 1 ? t("form.selected") : t("form.selectedPlural")}
      </Tag>
    );
  }, [selectedAssistants.length, t]);

  return (
    <div className="rutas-step rutas-step2">
      <Space align="baseline" wrap className="rutas-step2__header">
        <Typography.Title level={4} className="rutas-step__title">
          {t("form.selectAssistantsTitle")}
        </Typography.Title>
        {countTag}
      </Space>

      <Typography.Paragraph className="rutas-step__subtitle">
        {t("form.routeCreated", { name: route?.nameRute })}
      </Typography.Paragraph>

      <div className="rutas-step2__toolbar">
        <Segmented
          value={viewMode}
          onChange={(value) => setViewMode(value)}
          options={[
            { label: t("form.assistantsEduca360"), value: "edu360" },
            { label: t("form.myAssistants"), value: "mine" },
          ]}
          className="rutas-step2__segmented"
        />
      </div>

      <div className="rutas-step2__list">
        {loadingList ? (
          <div className="rutas-step__center">
            <Spin size="large" />
          </div>
        ) : assistants.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t("form.noAssistants")}
          />
        ) : (
          <List
            itemLayout="horizontal"
            dataSource={assistants}
            renderItem={(assistant) => {
              const checked = selectedSet.has(assistant._id);
              return (
                <List.Item
                  key={assistant._id}
                  className="rutas-step2__assistant-row"
                  onClick={() => handleToggleAssistant(assistant._id)}
                >
                  <List.Item.Meta
                    avatar={<Avatar src={assistant.miniature} size={44} />}
                    title={
                      <span
                        className="rutas-step2__assistant-title"
                        title={assistant.title}
                      >
                        {assistant.title}
                      </span>
                    }
                    description={
                      <span className="rutas-step2__assistant-path">
                        {assistant.path}
                      </span>
                    }
                  />
                  <Switch
                    checked={checked}
                    onClick={(v, e) => e?.stopPropagation?.()}
                    onChange={() => handleToggleAssistant(assistant._id)}
                  />
                </List.Item>
              );
            }}
          />
        )}
      </div>

      <div className="rutas-step__footer">
        <Space>
          <Button
            onClick={onClose}
            size="large"
            disabled={saving || loadingList}
          >
            {t("editStep1.cancelButton")}
          </Button>

          <Button
            type="primary"
            onClick={handleSaveAndNext}
            size="large"
            loading={saving}
            disabled={loadingList}
            icon={<ArrowRightOutlined />}
          >
            {t("editStep1.nextButton")}
          </Button>
        </Space>
      </div>
    </div>
  );
}
